import React from 'react';
import { useAdvancedMarkerSupported } from './AdvancedMarkerSupportContext';
function toLatLngLiteral(position) {
    if (position == null) {
        return undefined;
    }
    if (typeof position.lat === 'function') {
        const p = position;
        return { lat: p.lat(), lng: p.lng() };
    }
    const lit = position;
    if (typeof lit.lat === 'number' && typeof lit.lng === 'number') {
        return { lat: lit.lat, lng: lit.lng };
    }
    return undefined;
}
function buildMarkerContent(icon) {
    const root = document.createElement('div');
    root.style.cursor = 'pointer';
    root.style.pointerEvents = 'auto';
    if (typeof icon === 'string') {
        const img = document.createElement('img');
        img.src = icon;
        img.alt = '';
        img.style.display = 'block';
        root.appendChild(img);
        return root;
    }
    if (icon && 'url' in icon && icon.url) {
        const img = document.createElement('img');
        img.src = icon.url;
        img.alt = '';
        if (icon.scaledSize) {
            img.width = icon.scaledSize.width;
            img.height = icon.scaledSize.height;
        }
        root.appendChild(img);
        return root;
    }
    const sym = icon;
    if (!sym) {
        const dot = document.createElement('div');
        dot.style.width = '14px';
        dot.style.height = '14px';
        dot.style.borderRadius = '50%';
        dot.style.backgroundColor = '#ea4335';
        dot.style.border = '2px solid #fff';
        root.appendChild(dot);
        return root;
    }
    if (typeof sym.path === 'number') {
        const dot = document.createElement('div');
        dot.style.width = '14px';
        dot.style.height = '14px';
        dot.style.borderRadius = '50%';
        dot.style.backgroundColor = sym.fillColor || '#ea4335';
        dot.style.border = '2px solid #fff';
        root.appendChild(dot);
        return root;
    }
    // Render an SVG-path Symbol as the AdvancedMarker content.
    //
    // google.maps.Symbol semantics: `path` is in arbitrary path-units, `scale` multiplies path units to pixels,
    // `anchor` is the in-path-units coordinate that aligns with the marker position, `rotation` is degrees
    // applied around the anchor. Crucially, different SVG icons in this project use very different unit
    // ranges (drone viewBox ~10000 units, hoverbike ~1000, asv 512), so a fixed viewBox like `0 0 32 32`
    // would render them at sub-pixel sizes. The fix below avoids any hardcoded viewBox by using
    // `overflow: visible` and an inner <g> transform that maps native path coordinates to display pixels.
    const pathD = sym.path;
    const scale = typeof sym.scale === 'number' ? sym.scale : 1;
    const rotation = typeof sym.rotation === 'number' ? sym.rotation : 0;
    const ax = sym.anchor?.x ?? 0;
    const ay = sym.anchor?.y ?? 0;
    // SVG element acts as a 1x1 anchor point with overflow visible so the path can render at any
    // size in any direction without being clipped.
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.style.overflow = 'visible';
    svg.style.display = 'block';
    svg.setAttribute('width', '1');
    svg.setAttribute('height', '1');
    // Transform applied right-to-left:
    //   1. rotate(rotation ax ay): rotate path around anchor (still in path coords)
    //   2. scale(scale): scale from origin (path units -> pixels)
    //   3. translate(-ax*scale -ay*scale): shift so the (now scaled) anchor sits at the SVG's (0,0)
    // After this, the SVG's (0,0) IS the anchor point of the rendered icon.
    const g = document.createElementNS('http://www.w3.org/2000/svg', 'g');
    g.setAttribute('transform', `translate(${-ax * scale} ${-ay * scale}) scale(${scale}) rotate(${rotation} ${ax} ${ay})`);
    const pathEl = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    pathEl.setAttribute('d', pathD);
    if (sym.fillColor) {
        pathEl.setAttribute('fill', sym.fillColor);
    }
    else {
        pathEl.setAttribute('fill', 'none');
    }
    pathEl.setAttribute('fill-opacity', String(sym.fillOpacity ?? 1));
    if (sym.strokeColor) {
        pathEl.setAttribute('stroke', sym.strokeColor);
    }
    pathEl.setAttribute('stroke-width', String(sym.strokeWeight ?? 0));
    pathEl.setAttribute('stroke-opacity', String(sym.strokeOpacity ?? 1));
    g.appendChild(pathEl);
    svg.appendChild(g);
    // Root container is a 0x0 point so AdvancedMarker positions its bottom-center at the marker location,
    // which equals its top-left when both dimensions are zero. The SVG (0,0) (anchor) therefore aligns
    // with the marker position.
    root.style.position = 'relative';
    root.style.width = '0';
    root.style.height = '0';
    root.appendChild(svg);
    return root;
}
function iconFingerprint(icon) {
    if (!icon) {
        return '';
    }
    if (typeof icon === 'string') {
        return icon;
    }
    if ('url' in icon && icon.url) {
        return icon.url;
    }
    const s = icon;
    return JSON.stringify({
        p: typeof s.path === 'string' ? s.path : String(s.path),
        scale: s.scale,
        // rotation は意図的に除外: 見出し回転が毎ポーリングで変わると iconKey が変わり、
        // マーカーが再生成されて開いている InfoWindow(ポップアップ)が閉じてしまう。
        // 回転は下の effect で DOM の transform を直接更新する。
        fill: s.fillColor,
        stroke: s.strokeColor,
        sw: s.strokeWeight,
        fo: s.fillOpacity,
        so: s.strokeOpacity,
        ax: s.anchor?.x,
        ay: s.anchor?.y,
    });
}
/**
 * AdvancedMarker の content 内の SVG <g> transform を新しい rotation で再構成して更新する。
 * マーカー自体は再生成しないため、開いている InfoWindow は閉じない。
 */
function updateAdvancedMarkerRotation(marker, icon) {
    if (typeof icon.path !== 'string') {
        return;
    }
    const root = marker.content;
    const g = root?.querySelector('svg g');
    if (!g) {
        return;
    }
    const scale = typeof icon.scale === 'number' ? icon.scale : 1;
    const rotation = typeof icon.rotation === 'number' ? icon.rotation : 0;
    const ax = icon.anchor?.x ?? 0;
    const ay = icon.anchor?.y ?? 0;
    g.setAttribute('transform', `translate(${-ax * scale} ${-ay * scale}) scale(${scale}) rotate(${rotation} ${ax} ${ay})`);
}
const Marker = ({ onDragEnd, onClick, popupContent, disablePopup, ...mapProps }) => {
    const supportsAdvancedMarkers = useAdvancedMarkerSupported();
    const legacyMarkerRef = React.useRef(null);
    const advancedMarkerRef = React.useRef(null);
    const infoWindowRef = React.useRef(null);
    const listenersRef = React.useRef([]);
    const domCleanupRef = React.useRef([]);
    const positionLiteral = React.useMemo(() => toLatLngLiteral(mapProps.position), [mapProps.position]);
    const iconKey = React.useMemo(() => iconFingerprint(mapProps.icon), [mapProps.icon]);
    const canShow = !!(mapProps.map && positionLiteral);
    React.useEffect(() => {
        if (!canShow) {
            listenersRef.current.forEach((l) => l.remove());
            listenersRef.current = [];
            if (infoWindowRef.current) {
                infoWindowRef.current.close();
                infoWindowRef.current = null;
            }
            if (advancedMarkerRef.current) {
                advancedMarkerRef.current.map = null;
                advancedMarkerRef.current = null;
            }
            if (legacyMarkerRef.current) {
                legacyMarkerRef.current.setMap(null);
                legacyMarkerRef.current = null;
            }
            return;
        }
        if (!supportsAdvancedMarkers) {
            const map = mapProps.map;
            listenersRef.current = [];
            const pos = positionLiteral;
            if (!pos) {
                return undefined;
            }
            const marker = new google.maps.Marker({
                map,
                position: pos,
                icon: mapProps.icon,
                draggable: !!(mapProps.draggable || onDragEnd),
                title: mapProps.title ?? '',
                zIndex: mapProps.zIndex,
            });
            legacyMarkerRef.current = marker;
            const iw = !disablePopup || onClick ? new google.maps.InfoWindow({ maxWidth: 280, content: popupContent || 'Loading...' }) : null;
            infoWindowRef.current = iw ?? null;
            if ((onClick || !disablePopup) && iw) {
                listenersRef.current.push(marker.addListener('click', () => {
                    onClick?.();
                    if (!disablePopup) {
                        iw.open({ map, anchor: marker });
                    }
                }));
            }
            if (onDragEnd) {
                listenersRef.current.push(marker.addListener('dragend', () => {
                    const p = marker.getPosition();
                    if (p) {
                        onDragEnd({ lat: p.lat(), lng: p.lng() });
                    }
                }));
            }
            return () => {
                listenersRef.current.forEach((l) => l.remove());
                listenersRef.current = [];
                if (infoWindowRef.current) {
                    infoWindowRef.current.close();
                    infoWindowRef.current = null;
                }
                if (legacyMarkerRef.current) {
                    legacyMarkerRef.current.setMap(null);
                    legacyMarkerRef.current = null;
                }
            };
        }
        let cancelled = false;
        const map = mapProps.map;
        void (async () => {
            const { AdvancedMarkerElement } = (await google.maps.importLibrary('marker'));
            if (cancelled) {
                return;
            }
            const pos = toLatLngLiteral(mapProps.position);
            if (!pos) {
                return;
            }
            const content = buildMarkerContent(mapProps.icon);
            const marker = new AdvancedMarkerElement({
                map,
                position: pos,
                content,
                gmpDraggable: !!(mapProps.draggable || onDragEnd),
                title: mapProps.title ?? '',
                zIndex: mapProps.zIndex ?? undefined,
            });
            advancedMarkerRef.current = marker;
            const listeners = [];
            if (!disablePopup || onClick) {
                const iw = new google.maps.InfoWindow({ maxWidth: 280, content: popupContent || 'Loading...' });
                infoWindowRef.current = iw;
                const onContentClick = (ev) => {
                    ev.stopPropagation();
                    onClick?.();
                    if (!disablePopup) {
                        iw.open({ map, anchor: marker });
                    }
                };
                content.addEventListener('click', onContentClick);
                domCleanupRef.current.push(() => content.removeEventListener('click', onContentClick));
            }
            if (onDragEnd) {
                listeners.push(marker.addListener('dragend', () => {
                    const p = marker.position;
                    const lat = typeof p?.lat === 'function' ? p.lat() : p?.lat;
                    const lng = typeof p?.lng === 'function' ? p.lng() : p?.lng;
                    if (typeof lat === 'number' && typeof lng === 'number') {
                        onDragEnd({ lat, lng });
                    }
                }));
            }
            listenersRef.current = listeners;
        })();
        return () => {
            cancelled = true;
            domCleanupRef.current.forEach((dispose) => dispose());
            domCleanupRef.current = [];
            listenersRef.current.forEach((l) => l.remove());
            listenersRef.current = [];
            if (infoWindowRef.current) {
                infoWindowRef.current.close();
                infoWindowRef.current = null;
            }
            if (advancedMarkerRef.current) {
                advancedMarkerRef.current.map = null;
                advancedMarkerRef.current = null;
            }
        };
    }, [
        canShow,
        supportsAdvancedMarkers,
        mapProps.map,
        iconKey,
        mapProps.draggable,
        mapProps.title,
        mapProps.zIndex,
        disablePopup,
        onClick,
        onDragEnd,
    ]);
    React.useEffect(() => {
        infoWindowRef.current?.setContent(popupContent || '');
    }, [popupContent]);
    React.useEffect(() => {
        const adv = advancedMarkerRef.current;
        const leg = legacyMarkerRef.current;
        const pos = toLatLngLiteral(mapProps.position);
        if (adv) {
            if (pos) {
                adv.position = pos;
            }
            const sym = mapProps.icon;
            if (sym && typeof sym.path === 'string') {
                updateAdvancedMarkerRotation(adv, sym);
            }
        }
        if (leg) {
            if (pos) {
                leg.setPosition(pos);
            }
            // legacy marker は setIcon で回転を反映(InfoWindow は閉じない)
            if (mapProps.icon) {
                leg.setIcon(mapProps.icon);
            }
        }
    }, [positionLiteral?.lat, positionLiteral?.lng, mapProps.position, iconKey, mapProps.icon?.rotation]);
    return null;
};
export default Marker;
